import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ShieldCheck, Loader2, X, ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import { NoticeVariant } from "@/types";
import { VerifyAdmissionApplication } from "@/types/admissions";
import { ProgrammeModel } from "@/models/programme.model";
import { ProgrammeSelectModal } from "@/components/landing/application/ProgrammeSelectModal";


interface NoticeState {
  message: string;
  variant: NoticeVariant;
}

interface SendOtpResult {
  message: string;
  sessionId?: string;
  expiresAt?: string;
}

interface VerifyOtpResult {
  success: boolean;
  message: string;
  variant?: NoticeVariant;
  errorMessage?: string;
  sessionId?: string;
  draftAppId?: string;
  appId?: string;
  application?: VerifyAdmissionApplication | null;
  programmeCohortId?: string | null;
}

interface EmailVerificationGateProps {
  onVerified: (result: {
    email: string;
    message: string;
    sessionId?: string;
    draftAppId?: string;
    appId?: string;
    variant?: NoticeVariant;
    application?: VerifyAdmissionApplication | null;
    programmeCohortId?: string | null;
  }) => void;
  className?: string;
  requestOtp?: (email: string) => Promise<SendOtpResult>;
  confirmOtp?: (params: { email: string; code: string; sessionId?: string }) => Promise<VerifyOtpResult>;
  programmes?: ProgrammeModel[];
  isProgrammeLoading?: boolean;
  selectedProgrammeId?: string;
  selectedProgrammeName?: string;
  onProgrammeSelect?: (programme: { id?: string | null; title?: string | null }) => void;
}

const noticeStyles: Record<NoticeVariant, string> = {
  info: "border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]",
  success: "border-[#CBEED8] bg-[#EAF6EE] text-[#166534]",
  error: "border-red-200 bg-red-50 text-red-600",
};

const noticeIcon = {
  info: <Mail className="h-4 w-4 text-[#1D4ED8]" />,
  success: <ShieldCheck className="h-4 w-4 text-[#16A34A]" />,
  error: <Mail className="h-4 w-4 text-red-500" />,
};

export function EmailVerificationGate({
  onVerified,
  className,
  requestOtp,
  confirmOtp,
  programmes,
  isProgrammeLoading,
  selectedProgrammeId,
  selectedProgrammeName,
  onProgrammeSelect,
}: EmailVerificationGateProps) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [expectedCode, setExpectedCode] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isProgrammeModalOpen, setIsProgrammeModalOpen] = useState(false);

  const isSubmitDisabled = useMemo(
    () => isSending || isVerifying,
    [isSending, isVerifying],
  );

  const validateEmail = (value: string) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(value);
  };

  const resolvedProgrammeName = useMemo(() => {
    const match = programmes?.find(
      (programme) => programme.id === selectedProgrammeId,
    );
    if (selectedProgrammeName) {
      return selectedProgrammeName;
    }
    if (match?.title) {
      return match.title;
    }
    return selectedProgrammeId ?? "";
  }, [programmes, selectedProgrammeId, selectedProgrammeName]);

  const handleProgrammeSelection = (programme: ProgrammeModel) => {
    onProgrammeSelect?.({
      id: programme.id ?? "",
      title: programme.title,
    });
    setIsProgrammeModalOpen(false);
    if (otpSent) {
      setOtpSent(false);
      setSessionId(null);
      setCode("");
      setNotice({
        message: "Programme updated. Request a new code to continue.",
        variant: "info",
      });
    }
    setError(null);
  };

  const handleSendOtp = async () => {
    if (!selectedProgrammeId) {
      setError("Please choose a programme to continue.");
      setNotice(null);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      setNotice(null);
      return;
    }

    setError(null);
    setExpectedCode(null);
    setIsSending(true);
    setNotice({
      message: "Sending verification code...",
      variant: "info",
    });

    try {
      if (requestOtp) {
        const response = await requestOtp(email);
        setSessionId(response.sessionId ?? null);
        setNotice({
          message: response.message,
          variant: "info",
        });
      } else {
        const generated = Math.floor(100000 + Math.random() * 900000).toString();
        setExpectedCode(generated);
        await new Promise((resolve) => setTimeout(resolve, 800));
        setNotice({
          message: `A 6-digit verification code has been sent to ${email}.`,
          variant: "info",
        });
        setSessionId("local-session");
      }

      setOtpSent(true);
      setCode("");
    } catch (err: any) {
      setNotice(null);
      setError(
        err?.message ??
          "We couldn't send the verification code right now. Please try again.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpSent) {
      setError("Please request a verification code first.");
      return;
    }

    if (!selectedProgrammeId) {
      setError("Please choose a programme before verifying.");
      return;
    }

    if (code.trim().length !== 6 || !/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code sent to your email.");
      return;
    }

    setError(null);
    setIsVerifying(true);
    setNotice({
      message: "Verifying code...",
      variant: "info",
    });

    if (!sessionId && confirmOtp) {
      setNotice(null);
      setIsVerifying(false);
      setError("Verification session expired. Please request a new code.");
      return;
    }

    let verifiedPayload:
      | {
          email: string;
          message: string;
          sessionId?: string;
          draftAppId?: string;
          appId?: string;
          variant?: NoticeVariant;
          application?: VerifyAdmissionApplication | null;
          programmeCohortId?: string | null;
        }
      | null = null;

    try {
      let result: VerifyOtpResult;

      if (confirmOtp) {
        result = await confirmOtp({ email, code, sessionId: sessionId ?? undefined });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 800));
        // const success = expectedCode !== null && expectedCode === code;
        const success = true; 
        result = success
          ? {
              success: true,
              message: "Email verified successfully! You can continue your application.",
              variant: "success",
              sessionId: sessionId ?? undefined,
            }
          : {
              success: false,
              message: "",
              errorMessage: "That code doesn’t match. Please try again.",
            };
      }

      if (!result.success) {
        setNotice(null);
        setError(
          result.errorMessage ??
            "We couldn't verify that code. Please request a new one.",
        );
        setIsVerifying(false);
        return;
      }

      setNotice({
        message: result.message,
        variant: result.variant ?? "success",
      });

      verifiedPayload = {
        email,
        message: result.message,
        sessionId: result.sessionId ?? sessionId ?? undefined,
        draftAppId: result.draftAppId,
        appId: result.appId,
        variant: result.variant ?? "success",
        application: result.application,
        programmeCohortId: result.programmeCohortId,
      };
    } catch (err: any) {
      setNotice(null);
      setError(
        err?.message ?? "Something went wrong while verifying. Please try again.",
      );
    }

    setIsVerifying(false);

    if (verifiedPayload) {
      setOtpSent(false);
      setCode("");
      setExpectedCode(null);
      setSessionId(verifiedPayload.sessionId ?? sessionId ?? null);
      onVerified(verifiedPayload);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (otpSent) {
      await handleVerifyOtp();
    } else {
      await handleSendOtp();
    }
  };

  const handleResend = async () => {
    if (isSending || isVerifying) return;
    await handleSendOtp();
  };

  return (
    <div
      className={cn(
        "rounded-[32px] bg-white p-8 md:p-12 shadow-[0_20px_40px_rgba(15,23,42,0.08)] space-y-8",
        className,
      )}
    >
      <div className="space-y-3">
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#945268]">
          Begin your application
        </p>
        <h1 className="text-3xl font-semibold text-[#4E1D31]">
          Verify your email to continue
        </h1>
        <p className="text-sm text-slate-600">
          We'll send a 6-digit confirmation code to make sure you can access updates
          about your admission.
        </p>
      </div>

      {notice && (
        <div
          className={cn(
            "flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-sm",
            noticeStyles[notice.variant],
          )}
        >
          <div className="flex items-center gap-2">
            {noticeIcon[notice.variant]}
            <span>{notice.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="rounded-full p-1 text-current transition hover:bg-black/5"
            aria-label="Dismiss message"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Programme
            </label>
            <button
              type="button"
              onClick={() => setIsProgrammeModalOpen(true)}
              className={cn(
                "flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition",
                "hover:border-[#7B2A40] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#7B2A40]",
              )}
            >
              <div className="flex flex-col">
                {/* <span className="text-xs uppercase tracking-[0.08em] text-slate-500">
                  Select programme
                </span> */}
                <span
                  className={cn(
                    "text-base",
                    resolvedProgrammeName ? "text-slate-900" : "text-slate-400",
                  )}
                >
                  {resolvedProgrammeName || "Choose a programme"}
                </span>
              </div>
              {isProgrammeLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
          </div>

          <div className="space-y-2">
            <label
              className="text-sm font-medium text-slate-700"
              htmlFor="application-email"
            >
              Email Address
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#7B2A40] text-white">
                <Mail className="h-5 w-5" />
              </span>
              <Input
                id="application-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="applicant@email.com"
              />
            </div>
          </div>

          {otpSent && (
            <div className="space-y-2">
              <label
                className="text-sm font-medium text-slate-700"
                htmlFor="application-otp"
              >
                Enter verification code
              </label>
              <Input
                id="application-otp"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="Enter 6-digit code"
                className="h-12 rounded-xl border border-[#E4E7EC] bg-white text-center text-lg tracking-widest focus:border-[#7B2A40] focus:ring-[#7B2A40]"
              />
              <p className="text-xs text-slate-500">
                Didn't receive it?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  className="font-medium text-[#7B2A40] hover:underline disabled:text-slate-400 disabled:no-underline"
                  disabled={isSubmitDisabled}
                >
                  Resend code
                </button>
              </p>
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitDisabled}
          className="h-12 w-full rounded-xl bg-[#7B2A40] text-base font-semibold text-white shadow-sm transition hover:bg-[#651F34] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {(isSending || isVerifying) ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              {otpSent ? "Verifying..." : "Sending code..."}
            </span>
          ) : (
            otpSent ? "Verify email" : "Send verification code"
          )}
        </Button>
      </form>

      <ProgrammeSelectModal
        isOpen={isProgrammeModalOpen}
        onClose={() => setIsProgrammeModalOpen(false)}
        onSelect={handleProgrammeSelection}
        selectedProgrammeId={selectedProgrammeId}
        initialProgrammes={programmes}
        isInitialLoading={isProgrammeLoading}
      />
    </div>
  );
}
